import { useCallback, useEffect, useState } from "react";
import type { GetToken } from "./eqmsApi";
import {
  changeTestCaseStatus,
  createTestCase,
  deleteTestCase,
  friendlyMessage,
  getTestCase,
  listTestCases,
  saveTestSteps,
  updateTestCase,
} from "./eqmsApi";
import type {
  CreateTestCaseRequest,
  SaveStepsRequest,
  TestCase,
  TestCaseStatus,
  UpdateTestCaseRequest,
  WorkAreaType,
} from "../models/eqms";

/** Filters applied to the test-case registry list. */
export interface TestCaseFilters {
  status: TestCaseStatus | "";
  q: string;
}

/**
 * Orchestration for the test-case (design/test lane) registry: list + filters and the full CRUD /
 * lifecycle / step-editing handlers. The view holds no data-loading logic — it renders this state and
 * calls the handlers. Scoped to a project + work area (the open cybersecurity/SW/AI lane).
 */
export function useTestCases(getToken: GetToken, projectId: string | null, workArea: WorkAreaType | null) {
  const [cases, setCases] = useState<TestCase[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [filters, setFilters] = useState<TestCaseFilters>({ status: "", q: "" });

  const reload = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    setError(null);
    try {
      const rows = await listTestCases(getToken, projectId, {
        workArea: workArea ?? undefined,
        status: filters.status || undefined,
        q: filters.q || undefined,
      });
      setCases(rows);
    } catch (e) {
      setError(friendlyMessage(e));
    } finally {
      setLoading(false);
    }
  }, [getToken, projectId, workArea, filters.status, filters.q]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const create = useCallback(async (req: CreateTestCaseRequest): Promise<TestCase | null> => {
    if (!projectId) return null;
    setBusy(true);
    setError(null);
    try {
      const created = await createTestCase(getToken, projectId, req);
      await reload();
      return created;
    } catch (e) {
      setError(friendlyMessage(e));
      return null;
    } finally {
      setBusy(false);
    }
  }, [getToken, projectId, reload]);

  const load = useCallback((id: string) => getTestCase(getToken, id), [getToken]);

  const update = useCallback(async (id: string, req: UpdateTestCaseRequest): Promise<TestCase | null> => {
    setBusy(true);
    setError(null);
    try {
      const updated = await updateTestCase(getToken, id, req);
      await reload();
      return updated;
    } catch (e) {
      setError(friendlyMessage(e));
      return null;
    } finally {
      setBusy(false);
    }
  }, [getToken, reload]);

  const saveSteps = useCallback(async (id: string, req: SaveStepsRequest): Promise<TestCase | null> => {
    setBusy(true);
    setError(null);
    try {
      const saved = await saveTestSteps(getToken, id, req);
      return saved;
    } catch (e) {
      setError(friendlyMessage(e));
      return null;
    } finally {
      setBusy(false);
    }
  }, [getToken]);

  const changeStatus = useCallback(async (id: string, status: TestCaseStatus): Promise<TestCase | null> => {
    setBusy(true);
    setError(null);
    try {
      const updated = await changeTestCaseStatus(getToken, id, status);
      await reload();
      return updated;
    } catch (e) {
      setError(friendlyMessage(e));
      return null;
    } finally {
      setBusy(false);
    }
  }, [getToken, reload]);

  const remove = useCallback(async (id: string): Promise<boolean> => {
    setBusy(true);
    setError(null);
    try {
      await deleteTestCase(getToken, id);
      await reload();
      return true;
    } catch (e) {
      setError(friendlyMessage(e));
      return false;
    } finally {
      setBusy(false);
    }
  }, [getToken, reload]);

  return {
    cases, loading, error, busy, filters, setFilters,
    reload, create, load, update, saveSteps, changeStatus, remove,
    clearError: () => setError(null),
  };
}

export type TestCasesController = ReturnType<typeof useTestCases>;
